import React, { useEffect, useRef } from 'react';
import { useI18n } from '../i18n';

/* Reusable inline video with tap-to-play/pause – shows first frame */
const InlineVideo = ({ src }) => {
  const videoRef = useRef(null);

  // Force the browser to decode and paint the first frame by briefly
  // playing then immediately pausing. This is the most reliable cross-browser
  // method (covers Safari, iOS, Chrome).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const forceFrame = () => {
      // play() returns a promise; once playback starts we immediately pause
      const p = v.play();
      if (p !== undefined) {
        p.then(() => {
          v.pause();
          v.currentTime = 0;
        }).catch(() => {
          // Autoplay blocked – fall back to seek
          v.currentTime = 0.001;
        });
      }
    };

    if (v.readyState >= 3) {
      // Enough data already buffered
      forceFrame();
    } else {
      v.addEventListener('canplay', forceFrame, { once: true });
      return () => v.removeEventListener('canplay', forceFrame);
    }
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  return (
    <div className="about-image about-video-inline" onClick={togglePlay} style={{ cursor: 'pointer' }}>
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="auto"
      >
        <source src={`${src}#t=0.001`} type="video/mp4" />
      </video>
    </div>
  );
};

const AboutTab = () => {
  const sectionsRef = useRef([]);

  const videoUrl = (fileName) => `${process.env.PUBLIC_URL}/videos/${fileName}`;

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  const { t } = useI18n();
  return (
    <div className="about-page">
      {/* Video Hero Section – full-width background video */}
      <section className="about-hero about-hero-video animate-in" ref={addToRefs}>
        <video
          className="hero-background-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src={videoUrl('fullview.mp4')} type="video/mp4" />
        </video>
        <div className="hero-video-overlay" />
        <div className="about-hero-content">
          <h1 className="about-hero-title">{t('about_hero_title')}</h1>
          <p className="about-hero-subtitle">{t('about_hero_subtitle')}</p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="unified-stats-section" ref={addToRefs}>
        <div className="unified-stats-container">
          <div className="unified-stats-grid">
            <div className="unified-stat-card">
              <span className="unified-stat-number">200+</span>
              <span className="unified-stat-label">{t('stats_total_land')}</span>
            </div>
            <div className="unified-stat-card">
              <span className="unified-stat-number">90+</span>
              <span className="unified-stat-label">{t('stats_utilities')}</span>
            </div>
            <div className="unified-stat-card">
              <span className="unified-stat-number">18+</span>
              <span className="unified-stat-label">{t('stats_residential')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 – Video Right (hover to play) */}
      <section className="about-section section-right" ref={addToRefs}>
        <div className="about-section-content">
          <div className="about-text">
            <h2 className="section-title">{t('about_loc_title')}</h2>
            <p className="section-description">{t('about_loc_desc')}</p>
          </div>
          <InlineVideo src={videoUrl('overview-1.mp4')} />
        </div>
      </section>

      {/* Section 2 – Video Left */}
      <section className="about-section section-left" ref={addToRefs}>
        <div className="about-section-content">
          <InlineVideo src={videoUrl('overview-2.mp4')} />
          <div className="about-text">
            <h2 className="section-title">{t('about_why_title')}</h2>
            <div className="section-description">
              <p><strong>{t('about_why_item1_title')}</strong><br />
              {t('about_why_item1_desc')}</p>
              
              <p><strong>{t('about_why_item2_title')}</strong><br />
              {t('about_why_item2_desc')}</p>
              
              <p><strong>{t('about_why_item3_title')}</strong><br />
              {t('about_why_item3_desc')}</p>
              
              <p><strong>{t('about_why_item4_title')}</strong><br />
              {t('about_why_item4_desc')}</p>
              
              <p><strong>{t('about_why_item5_title')}</strong><br />
              {t('about_why_item5_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 – Video Right */}
      <section className="about-section section-right" ref={addToRefs}>
        <div className="about-section-content">
          <div className="about-text">
            <h2 className="section-title">{t('about_trust_title')}</h2>
            <div className="section-description">
              <p>{t('about_trust_desc1')}</p>
              {t('about_trust_desc2') && <p><strong>{t('about_trust_desc2')}</strong></p>}
              <p><strong>{t('about_trust_b1')}</strong></p>
              <p><strong>{t('about_trust_b2')}</strong></p>
              <p><strong>{t('about_trust_b3')}</strong></p>
            </div>
          </div>
          <InlineVideo src={videoUrl('overview-3.mp4')} />
        </div>
      </section>

      {/* Section 4 – Explore CTA */}
      <section className="about-section section-full" ref={addToRefs}>
        <div className="about-section-content">
          <div className="about-text-center">
            <h2 className="section-title">{t('about_explore_title')}</h2>
            <p className="section-description">{t('about_explore_desc')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutTab;
